import React, { CSSProperties, useMemo } from 'react';
import styles from './footer.module.scss';
import { MessageKeys, useTranslations } from 'next-intl';
import Link from 'next/link';
import { IFooter } from '../../../models/footer/IFooter';
import { INTOWhiteLogo } from '../../atoms/icons/icons';
import IMenuItem from '../../../models/header/IMenuItem';
import { ISocialMediaLinks } from '../../../models/social-media-links/ISocialMediaLinks';
import SocialMediaLinks from '../../molecules/social-media-links/social-media-links';
import { Country } from '../../../constants/country';
import { ApplicationType } from '../../../constants/application-type';
import Image from 'next/image';

export type Props = {
  footerProps: IFooter;
};

export default function Footer({ footerProps }: Props) {
  const footerStyle = useMemo(() => {
    return {
      '--footer-color': footerProps?.themeColor,
      '--footer-copy-color': footerProps?.themeCopyColor,
    } as CSSProperties;
  }, [footerProps.themeColor, footerProps.themeCopyColor]);

  return (
    <footer className={styles.footer} style={footerStyle}>
      <div className='container p-4'>
        <div
          className={`d-flex flex-md-row flex-column justify-content-between align-items-md-center ${
            footerProps?.pageURLs ? 'mb-3' : ''
          }`}
        >
          <div>
            <INTOLogo logoURL={footerProps?.intoLogoURL} application={footerProps?.application} />
          </div>
          <SocialLinks socialURLs={footerProps?.socialURLs} />
        </div>

        <PageLinks pageURLs={footerProps?.pageURLs} />

        {footerProps?.showB2C && (
          <B2CText
            currentLocale={footerProps?.currentLocale}
            countryCode={footerProps?.countryCode}
          />
        )}

        <FooterCopyright
          textICP={footerProps?.textICP}
          legalURLs={footerProps?.legalURLs}
          codePSB={footerProps?.codePSB}
        />
      </div>
    </footer>
  );
}

type INTOLogoProps = {
  logoURL: string;
  application: ApplicationType;
};

function INTOLogo({ logoURL, application }: INTOLogoProps) {
  const footerLogo = useMemo(() => {
    switch (application) {
      case ApplicationType.UniBrandedB2c:
        return (
          <Image
            src='https://media.intostudy.com/image/upload/v1738582515/IPW-INTO-landscape-logo-reverse-rgb.svg'
            alt='INTO University Partnerships'
            width={260}
            height={48}
          />
        );
      default:
        return <INTOWhiteLogo />;
    }
  }, [application]);

  return logoURL ? (
    <Link href={logoURL} title='INTO University Partnerships' className='d-inline-flex'>
      {footerLogo}
    </Link>
  ) : (
    footerLogo
  );
}

type SocialLinksProps = {
  socialURLs: ISocialMediaLinks;
};

function SocialLinks({ socialURLs }: SocialLinksProps) {
  return (
    <div className={styles['footer-social-links']}>
      <SocialMediaLinks socialMediaLinks={socialURLs} />
    </div>
  );
}

type PageLinksProps = {
  pageURLs: IMenuItem[];
};

function PageLinks({ pageURLs }: PageLinksProps) {
  const translate = useTranslations(`Common`);

  if (!pageURLs || pageURLs.length === 0) return null;

  return (
    <div className={styles[`top-links`]}>
      {pageURLs.map((item) => (
        <Link key={item.url} href={item.url}>
          {translate(item.translationKey as MessageKeys<any, any>)}
        </Link>
      ))}
    </div>
  );
}

type B2CTextProps = {
  currentLocale: string;
  countryCode?: string;
};

function B2CText({ currentLocale, countryCode }: B2CTextProps) {
  const translate = useTranslations(`Common`);

  // TODO: Use SupportedLanguages from the IntoStudy package
  const isEnUS = currentLocale == 'en' && countryCode === Country.US;

  return (
    <div className={`${styles[`contact-text`]} col-12 pt-3`}>
      <p>
        {translate(
          (isEnUS ? `footer.student-copy.prefix-us` : `footer.student-copy.prefix`) as MessageKeys<
            any,
            any
          >,
        )}
        <Link href={`/${currentLocale}/contact` as MessageKeys<any, any>}>please contact us</Link>
        {translate(`footer.student-copy.suffix` as MessageKeys<any, any>)}
      </p>
      <p>
        {translate(
          (isEnUS ? `footer.counsellor-copy-us` : `footer.counsellor-copy`) as MessageKeys<
            any,
            any
          >,
        )}
      </p>
    </div>
  );
}

type FooterCopyrightProps = {
  legalURLs: IMenuItem[];
  textICP: string;
  codePSB?: string;
};

function FooterCopyright({ textICP, legalURLs, codePSB }: FooterCopyrightProps) {
  const translate = useTranslations(`Common`);

  return (
    <div className={styles['footer-copyright']}>
      <div className='row pt-3'>
        <div className='col-md-12'>
          {translate(`footer.copyright`, {
            year: new Date().getFullYear(),
          })}
          <Link
            href='https://beian.miit.gov.cn/'
            target='_blank'
            title='ICP/IP地址/域名信息备案管理系统'
            className='me-1'
          >
            {textICP}
          </Link>
          {codePSB && (
            <Link
              href={`https://beian.mps.gov.cn/#/query/webSearch?code=${codePSB}`}
              target='_blank'
              title={`沪公网安备${codePSB}号`}
              className={`me-1 ${styles['has-separator']}`}
            >
              <img
                src='https://media.intostudy.com/image/upload/v1741068665/beian.png'
                alt='PSB icon - beian'
              />{' '}
              {`沪公网安备${codePSB}号`}
            </Link>
          )}
          {legalURLs && legalURLs.length > 0 && (
            <ul>
              {legalURLs.map((item) => (
                <li key={item.url}>
                  <Link className='mr-1' href={item.url}>
                    {translate(item.translationKey as MessageKeys<any, any>)}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
